"use client"

import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { RefreshCw, Calendar } from "lucide-react"
import { Card } from "@/components/Card"
import { RecentClients } from "@/pages/Dashboard/Clientes/components/RecentClients.tsx"
import "./DashboardHome.css"
import MonthlyMetricsCards from "./components/MonthlyMetricsCards"
import YearlyMetricsCards from "./components/YearlyMetricsCards"
import RevenueByServiceChart from "./components/RevenueByServiceChart"

const MONTHS = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
]

const DashboardHome = () => {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const queryClient = useQueryClient()

  // Default to current month/year
  const now = new Date()
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1)
  const [selectedYear, setSelectedYear] = useState(now.getFullYear())

  // Generate years for dropdown (last 3 years + current)
  const currentYear = now.getFullYear()
  const years = [currentYear - 2, currentYear - 1, currentYear]

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['monthlyMetrics'] }),
        queryClient.refetchQueries({ queryKey: ['yearlyMetrics'] }),
        queryClient.refetchQueries({ queryKey: ['revenueByService'] }),
        queryClient.refetchQueries({ queryKey: ['recentClients'] }),
      ])
    } catch (error) {
      console.error('Error refreshing dashboard:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  return (
    <section className="dashboard-home-page">
      <div className="dashboard-header dashboard-home-header">
        <div className="dashboard-home-heading-wrap">
          <h1 className="gradient-heading">Dashboard</h1>
          <p className="dashboard-home-subtitle">
            Acompanhe receitas, crescimento e saude das inscricoes em tempo real.
          </p>
        </div>
        <button 
          className={`refresh-button ${isRefreshing ? 'refreshing' : ''}`}
          onClick={handleRefresh}
          disabled={isRefreshing}
          title="Atualizar dados"
        >
          <RefreshCw size={18} />
          Atualizar
        </button>
      </div>

      {/* Month/Year Filter Section */}
      <div className="dashboard-filter-section dashboard-filter-shell">
        <div className="filter-header">
          <Calendar size={20} />
          <span>Métricas Mensais</span>
        </div>
        <div className="filter-controls">
          <select 
            value={selectedMonth} 
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="filter-select"
          >
            {MONTHS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="filter-select"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="dashboard-content">
        {/* Top metrics cards (3 monthly + 2 yearly) */}
        <div className="top-metrics-row">
          <MonthlyMetricsCards month={selectedMonth} year={selectedYear} />
          <YearlyMetricsCards year={selectedYear} />
        </div>
        
        {/* Main chart row */}
        <div className="charts-row">
          <Card className="chart-card">
            <div className="card-header">
              <h3 className="card-title">Faturamento por Serviço</h3>
              <p className="card-description">
                Receita por tipo de produto em {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}
              </p>
            </div>
            <div className="card-content">
              <RevenueByServiceChart month={selectedMonth} year={selectedYear} />
            </div>
          </Card>
        </div>
        
        {/* Recent Clients */}
        <Card className="clients-card">
          <div className="card-header">
            <h3 className="card-title">Clientes Recentes</h3>
            <p className="card-description">Últimos clientes adicionados ao sistema</p>
          </div>
          <div className="card-content">
            <RecentClients />
          </div>
        </Card>
      </div>
    </section>
  )
}

export default DashboardHome
